/** Same-place rental default time; must never be the only thing blocking Search. */
export const DEFAULT_CAR_RENTAL_TIME = '10:00';
export const DEFAULT_CAR_RENTAL_DAYS = 1;

function isValidHm(value) {
  const t = String(value || '').trim();
  if (!/^\d{2}:\d{2}$/.test(t)) return false;
  const [h, m] = t.split(':').map(Number);
  return h >= 0 && h <= 23 && m >= 0 && m <= 59;
}

function addDaysYmd(ymd, daysToAdd) {
  const raw = String(ymd || '').slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const [y, m, d] = raw.split('-').map(Number);
  const utc = Date.UTC(y, m - 1, d);
  const next = new Date(utc + Math.max(0, Number(daysToAdd) || 0) * 86400000);
  return [
    next.getUTCFullYear(),
    String(next.getUTCMonth() + 1).padStart(2, '0'),
    next.getUTCDate().toString().padStart(2, '0'),
  ].join('-');
}

function hmToMinutes(hhmm, fallback) {
  const [h, m] = String(hhmm || '').split(':').map(Number);
  if (!Number.isFinite(h) || !Number.isFinite(m)) return fallback;
  return Math.min(23 * 60 + 59, Math.max(0, h * 60 + m));
}

/**
 * Fill drop-off + times so a guest who only chose pick-up date and location can search.
 * Matches suggestCollectionFromDays for a morning 10:00 pickup (same-day return at 21:00 for 1 day).
 */
export function applyCarRentalSearchDefaults({
  pickupRef = '',
  dropoffRef = '',
  pickupDate = '',
  dropoffDate = '',
  pickupTime = '',
  dropoffTime = '',
  rentalDays = DEFAULT_CAR_RENTAL_DAYS,
} = {}) {
  const pul = String(pickupRef || '').trim();
  const pud = String(pickupDate || '').trim();
  const put = isValidHm(pickupTime) ? String(pickupTime).trim() : DEFAULT_CAR_RENTAL_TIME;
  const rdays = Math.max(1, parseInt(rentalDays, 10) || DEFAULT_CAR_RENTAL_DAYS);
  const dol = String(dropoffRef || '').trim() || pul;
  const pickupMins = hmToMinutes(put, 10 * 60);
  const suggestedDod = pud
    ? (pickupMins <= 12 * 60 ? addDaysYmd(pud, rdays - 1) : addDaysYmd(pud, rdays))
    : '';
  const suggestedDot = pud
    ? (pickupMins <= 12 * 60 ? '21:00' : put)
    : DEFAULT_CAR_RENTAL_TIME;
  const dod = String(dropoffDate || '').trim() || suggestedDod;
  const dot = isValidHm(dropoffTime) ? String(dropoffTime).trim() : suggestedDot;
  return {
    pickupRef: pul,
    dropoffRef: dol,
    pickupDate: pud,
    dropoffDate: dod,
    pickupTime: put,
    dropoffTime: dot,
    rentalDays: rdays,
  };
}

/** Compact hero / fleet: date + pick-up location (days, times, drop-off default). */
export function canSearchCarsFromHero({ pickupDate, pickupRef } = {}) {
  return Boolean(String(pickupDate || '').trim() && String(pickupRef || '').trim());
}

export function validateCarRentalHeroSearch({ pickupDate, pickupRef } = {}) {
  const missing = [];
  if (!String(pickupRef || '').trim()) missing.push({ key: 'pul', label: 'pick-up location' });
  if (!String(pickupDate || '').trim()) missing.push({ key: 'pud', label: 'pick-up date' });
  return { ok: missing.length === 0, missing };
}

export function nextCarRentalSearchFieldKey({ pickupDate, pickupRef } = {}) {
  if (!String(pickupRef || '').trim()) return 'pul';
  if (!String(pickupDate || '').trim()) return 'pud';
  return null;
}

export function carRentalSearchHint(missing) {
  const first = Array.isArray(missing) ? missing[0] : null;
  const label = first?.label || (typeof first === 'string' ? first : '');
  if (!label) return '';
  return `Add a ${label} to search.`;
}
